import { useState } from "react";
import { toast } from "sonner";
import { BellRing, Mail, MessageCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createReminder } from "@/lib/api";
import { fmtDate } from "@/lib/format";

export const ReminderModal = ({ event, open, onClose }) => {
  const [channel, setChannel] = useState("email");
  const [contact, setContact] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload =
        channel === "email"
          ? { channel, email: contact, event_id: event.id }
          : { channel, phone: contact, event_id: event.id };
      const res = await createReminder(payload);
      if (res.status === "already") {
        toast.info("Je staat al op de lijst voor dit event");
      } else if (res.email === "failed") {
        toast.success("Herinnering opgeslagen", {
          description: "De bevestigingsmail kon nu niet worden verstuurd, je herinnering staat wel vast.",
        });
      } else {
        toast.success("Herinnering ingesteld!", {
          description:
            channel === "email"
              ? `We mailen je zodra de verkoop van ${event.name} start.`
              : `Je staat op de WhatsApp-lijst voor ${event.name}.`,
        });
      }
      setContact("");
      onClose();
    } catch {
      toast.error("Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="reminder-modal"
        className="border-white/10 bg-[#12121C] text-white sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl">
            <BellRing className="h-5 w-5 text-volt" />
            Herinner mij
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Krijg een seintje zodra de kaartverkoop van{" "}
            <span className="font-semibold text-volt">{event?.name}</span> start.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {event?.sale_start && (
            <p className="rounded-lg border border-white/10 bg-black/40 p-3 font-mono text-xs text-slate-400">
              Verkoop start: <span className="text-volt">{fmtDate(event.sale_start, true)}</span>
            </p>
          )}
          <div className="flex overflow-hidden rounded-full border border-white/15">
            <button
              type="button"
              onClick={() => setChannel("email")}
              data-testid="reminder-channel-email"
              className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-bold transition-colors ${
                channel === "email" ? "bg-volt text-black" : "text-slate-400 hover:text-white"
              }`}
            >
              <Mail className="h-3.5 w-3.5" /> E-mail
            </button>
            <button
              type="button"
              onClick={() => setChannel("whatsapp")}
              data-testid="reminder-channel-whatsapp"
              className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-bold transition-colors ${
                channel === "whatsapp" ? "bg-volt text-black" : "text-slate-400 hover:text-white"
              }`}
            >
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
            </button>
          </div>
          <input
            required
            type={channel === "email" ? "email" : "tel"}
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            placeholder={channel === "email" ? "jij@voorbeeld.nl" : "+31 6 12345678"}
            data-testid={channel === "email" ? "reminder-email-input" : "reminder-phone-input"}
            className="w-full rounded-full border border-white/15 bg-black/50 px-4 py-3 text-sm text-white placeholder:text-slate-600 focus:border-volt focus:outline-none"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="reminder-submit-button"
            className="w-full rounded-full bg-volt py-3 text-sm font-bold text-black transition-transform hover:scale-[1.02] hover:bg-volt-hover disabled:opacity-50"
          >
            {loading ? "Bezig..." : "Stuur mij een herinnering"}
          </button>
          <p className="text-center text-[11px] text-slate-500">
            Geen spam. Alleen een seintje bij de start van de verkoop.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
